import {
  resetFlightsForm,
  resetFlightsResults,
  selectFormValidity,
} from "../searchFlightsSlice";
import React from "react";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit } from "@fortawesome/free-solid-svg-icons/faEdit";
import {
  ConfigSliceState,
  selectConfig,
  updateLanguage,
} from "../../config/ConfigSlice";
import i18n from "../../../i18n";
import queryString from "query-string";
import BookingButton from "../BookingButton";
import SearchFlightsResultsPrint from "../results/SearchFlightsResultsPrint";
import { useTranslation } from "react-i18next";
import OriginAirportInput from "./OriginAirportInput";
import DestinationAirportInput from "./DestinationAirportInput";
import ViaAirportInput from "./ViaAirportInput";
import DateRangeWeeklyInput from "./DateRangeWeeklyInput";
import RoundTripInput from "./RoundTripInput";
import SwitchAirportsButton from "./SwitchAirportsButton";
import DateRangeInput from "./DateRangeInput";
import NonStopFlightsInput from "./NonStopFlightsInput";
import IncludeCodeShareInput from "./IncludeCodeShareInput";
import SearchFlightsFormContent from "./SearchFlightsFormContent";
import ViaCheckbox from "./ViaCheckbox";
import DepartingTimeInput from "./DepartingTimeInput";
import ReturningTimeInput from "./ReturningTimeInput";
import ReturningDateInput from "./ReturningDateInput";
import DepartingDateInput from "./DepartingDateInput";
import SearchFlightsButton from "./SearchFlightsButton";
import TimeFormatCheckbox from "./TimeFormatCheckbox";
import useFormInitialization from "../useFormInitialization";
import DestinationAnywhereInput from "./DestinationAnywhereInput";
import FlightTypeToggleInput from "./FlightTypeToggleInput";
import { faList } from "@fortawesome/free-solid-svg-icons/faList";

function SearchFlightsForm() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const loading = useFormInitialization();

  const { mapper, components, app }: ConfigSliceState =
    useAppSelector(selectConfig);

  const formDisabled = useAppSelector(
    (state) => state.searchFlights.form.disabled
  );
  const resultsLoading = useAppSelector(
    (state) => state.searchFlights.results.loading
  );
  const destinationsResultsLoading = useAppSelector(
    (state) => state.searchFlights.destinationsResults.loading
  );
  const anywhere = useAppSelector(
    (state) => state.searchFlights.form.destinationAnywhere
  );

  const display = useAppSelector((state) => state.searchFlights.display);

  if (loading) {
    return null;
  }

  return (
    <div id="search-flights-form" className="box">
      {formDisabled && <SearchFlightsFormContent />}
      {!formDisabled && (
        <>
          {components.SearchPanel.HeaderStrap.display && (
            <div className="form-header">
              {t(components.SearchPanel.HeaderStrap.translation)}
            </div>
          )}
          <div className="block">
            {components.Inputs.RoundTrip.display && <RoundTripInput />}
            <div className="columns is-vcentered">
              <div className="column">
                <OriginAirportInput />
                <ViaAirportInput />
                <DestinationAirportInput />
                <DestinationAnywhereInput />
              </div>
              <div className="column is-narrow has-text-centered">
                <SwitchAirportsButton />
              </div>
            </div>

            <div className="field is-horizontal">
              <div className="field-body">
                <DepartingDateInput />
                <ReturningDateInput />
              </div>
            </div>
            <SplitDateError />
            <div className="field is-horizontal">
              <div className="field-body">
                {components.Inputs.RequestTime.display && (
                  <DepartingTimeInput />
                )}
                {components.Inputs.ReturnRequestTime.display && (
                  <ReturningTimeInput />
                )}
              </div>
            </div>
          </div>

          {components.Headers.FlightSearchFilter.display && (
            <div className="form-header" id="search-filter-form-header">
              {t("FILTER_FLIGHTS_BY_ANY_OF_THE_FOLLOWING")}
            </div>
          )}

          <div className="columns is-multiline pl-1 pr-1">
            {components.Inputs.DateRange.display && (
              <div className="column">
                <DateRangeInput />
              </div>
            )}
            {components.Inputs.DateRangeWeekly.display && (
              <div className="column">
                <DateRangeWeeklyInput />
              </div>
            )}
            {components.Inputs.NonStopFlights.display && (
              <div className="column">
                <NonStopFlightsInput
                  format={components.Inputs.NonStopFlights.format}
                />
              </div>
            )}
            {components.Inputs.IncludeCodeShare.display && (
              <div className="column">
                <IncludeCodeShareInput />
              </div>
            )}
            {mapper.showVia && (
              <div className="column">
                <ViaCheckbox />
              </div>
            )}
            {components.Inputs.TimeFormat?.display && (
              <div className="column">
                <TimeFormatCheckbox />
              </div>
            )}
            {mapper.enableFlightTypeToggle && (
              <div className="column">
                <FlightTypeToggleInput />
              </div>
            )}
          </div>
        </>
      )}
      <div id="button-container" className="columns is-mobile">
        {formDisabled && !resultsLoading && (
          <div className="column">
            <div
              id="print-button-content"
              className="field is-grouped is-grouped-left"
            >
              <div className="control">
                <SearchFlightsResultsPrint />
              </div>
            </div>
          </div>
        )}

        <div className="column">
          <div
            id="search-button-content"
            className="field is-grouped is-grouped-right"
          >
            {components.SearchPanel?.LanguageToggle?.display &&
              (!formDisabled || resultsLoading) && (
                <div className="control" style={{ marginRight: "auto" }}>
                  <button
                    type="button"
                    className="button is-small language-toggle-btn"
                    onClick={() => {
                      const currentLang = app.language || "en-AUS";
                      const isSpanish = currentLang.startsWith("es");
                      const targetLang = isSpanish
                        ? `en-${app.customerId}`
                        : `es-${app.customerId}`;

                      i18n.changeLanguage(targetLang);
                      dispatch(updateLanguage(targetLang));

                      const parsed = queryString.parse(window.location.search);
                      parsed.lng = targetLang;
                      window.history.replaceState(
                        null,
                        "",
                        `?${queryString.stringify(parsed)}`
                      );
                    }}
                  >
                    {(app.language || "en-AUS").startsWith("es")
                      ? "English"
                      : "Español"}
                  </button>
                </div>
              )}
            {(!formDisabled || resultsLoading) && (
              <div className="control">
                <button
                  id="clear-form-button"
                  disabled={resultsLoading}
                  className="button is-light is-small is-align-self-flex-end"
                  onClick={() => {
                    if (!resultsLoading) {
                      dispatch(resetFlightsForm());
                    }
                  }}
                >
                  <span>{t("CLEAR_FORM")}</span>
                </button>
              </div>
            )}
            {(!formDisabled || resultsLoading) && (
              <div className="control">
                <SearchFlightsButton />
              </div>
            )}
            {formDisabled && !resultsLoading && (
              <div id="button-group">
                <div className="control">
                  <button
                    id="modify-search-button"
                    className={`button${
                      destinationsResultsLoading ? " is-loading" : ""
                    }`}
                    disabled={destinationsResultsLoading}
                    onClick={() => {
                      dispatch(resetFlightsResults(display));
                    }}
                  >
                    <span className="icon">
                      <FontAwesomeIcon
                        icon={
                          display === "flights" && anywhere ? faList : faEdit
                        }
                      />
                    </span>
                    <span>
                      {display === "flights" && anywhere
                        ? t("DESTINATIONS_BACK")
                        : t("MODIFY_SEARCH")}
                    </span>
                  </button>
                </div>
                {mapper.bookingLink.url && display === "flights" && (
                  <div className="control" id="booking-control">
                    <BookingButton />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function SplitDateError() {
  const { t } = useTranslation();
  const { roundTripDateRangeRestriction } = useAppSelector(selectFormValidity);
  const splitDateValidationConfig = useAppSelector(
    (state) => state.config.components.Validation?.roundTripDateRangeRestriction
  );

  if (roundTripDateRangeRestriction) return null;

  return (
    <p className="help is-danger">
      {t(
        splitDateValidationConfig?.errorKey ?? "SEARCH_ALERT_SPLIT_DATE_BOOKING"
      )}
    </p>
  );
}

export default SearchFlightsForm;
